// Presentation-only mappings for Journey Lab. `journey-language` owns every mode/verdict word and proof
// boundary; this facade owns lifecycle tones, timestamps and compact formatting only.

import type { Tone } from './primitives.tsx';
import type {
  JourneyExecutionMode,
  JourneyLiveExecutionMode,
  JourneyRunState,
  JourneyStepState,
  JourneyVerdict,
} from './journey-lab-api.ts';
import { journeyModePresentation, journeyVerdictLabel } from './journey-language.ts';

export { journeyAvailableModeLabels } from './journey-language.ts';

export function journeyModeLabel(
  mode: JourneyExecutionMode,
  liveMode?: JourneyLiveExecutionMode,
): string {
  return journeyModePresentation(mode, liveMode).label;
}

export function journeyModeProofBoundary(
  mode: JourneyExecutionMode,
  liveMode?: JourneyLiveExecutionMode,
): string {
  return journeyModePresentation(mode, liveMode).proofBoundary;
}

export function journeyStatusLabel(state: JourneyRunState, verdict?: JourneyVerdict): string {
  if (state === 'queued') return 'Queued';
  if (state === 'running') return 'Running';
  if (state === 'interrupted') return 'Interrupted';
  return journeyVerdictLabel(verdict);
}

export function journeyStepStatusLabel(state: JourneyStepState, verdict?: JourneyVerdict): string {
  if (state === 'pending') return 'Pending';
  if (state === 'running') return 'Running';
  if (state === 'blocked') return 'Blocked before completion';
  return journeyVerdictLabel(verdict);
}

export function journeyTone(state: JourneyRunState, verdict?: JourneyVerdict): Tone {
  if (state === 'queued' || state === 'running') return 'attention';
  if (state === 'interrupted') return 'blocked';
  if (verdict === 'PASS') return 'go';
  return 'blocked';
}

export function verdictTone(verdict: JourneyVerdict | undefined): Tone {
  if (verdict === 'PASS') return 'go';
  return 'blocked';
}

export function journeyStepTone(state: JourneyStepState, verdict?: JourneyVerdict): Tone {
  if (state === 'running') return 'attention';
  if (state === 'pending') return 'neutral';
  if (state === 'blocked') return 'blocked';
  return verdictTone(verdict);
}

const STAMP_FORMAT = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
});

export function journeyStamp(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : STAMP_FORMAT.format(date);
}

export function shortDefinitionHash(value: string): string {
  return value.length > 12 ? `${value.slice(0, 12)}…` : value;
}

export function costLabel(pence: number | undefined): string {
  return pence === undefined ? 'Not recorded' : `£${(pence / 100).toFixed(2)}`;
}
